// ./src/components/features/DisplayConfigurations/DisplayFormModal/DisplayFormModal.tsx
import React, { useState, useEffect } from "react";
import {
  TextInput,
  Textarea,
  Stack,
  Text,
  Grid,
  Alert,
  InputLabel,
  Title,
} from "@mantine/core";

import {
  Display,
  DisplayInput,
  DisplayRunningMessage,
  DisplayRunningMessageInput,
  ContentBlock,
  DisplayLayout,
  DisplayZoneInput,
} from "@/utils/api";

import { AppModal, AppIcon } from "@/components/generic";
import { ContentBlockSelector } from "@/components/features/ContentBlocks";
import { DisplayLayoutSelector } from "@/components/features/DisplayLayouts";
import { DisplayRunningMessageSelector } from "@/components/features/DisplayRunningMessageSelector";
import { StatusSelector } from "@/components/features/StatusSelector";
import { ThemeSelector } from "@/components/features/Themes";
import { DisplayZoneAssignments } from "@/components/features/DisplayZoneAssignments";
import { DisplayZoneCardValue } from "@/components/features/DisplayZoneCard";
import { BlockTransitionAnimationSelector } from "../../BlockTransitionAnimationSelector";

export interface DisplayFormModalProps {
  /** Whether the modal is open */
  opened: boolean;

  /** Function to close the modal */
  onClose: () => void;

  /** Display to edit (if null/undefined, it's create mode) */
  display?: Display | null;

  /** Venue ID for the displays (required) */
  venueId: string;

  /** Callback when display should be created */
  onCreateDisplay?: (displayData: DisplayInput) => Promise<Display | null>;

  /** Callback when display should be updated */
  onUpdateDisplay?: (
    displayId: string,
    displayData: DisplayInput,
  ) => Promise<Display | null>;

  /** Whether create operation is in progress */
  isCreating?: boolean;

  /** Whether update operation is in progress */
  isUpdating?: boolean;

  /** Last error from display operations */
  lastError?: string | null;

  /** Callback to clear errors */
  onClearError?: () => void;
}

interface DisplayFormData {
  venueId: string | null | undefined;
  layoutId: string | null | undefined;
  themeId: string | null | undefined;
  name: string;
  description: string;
  emergencyContentBlockId: string | null;
  blockTransitionAnimation: string | null;
  status: number;
  displayRunningMessages: DisplayRunningMessageInput[] | null | undefined;
  displayZones?: DisplayZoneCardValue[] | null | undefined;
}

const initialFormData: DisplayFormData = {
  venueId: null,
  layoutId: null,
  themeId: null,
  name: "",
  description: "",
  emergencyContentBlockId: null,
  blockTransitionAnimation: null,
  status: 100,
  displayRunningMessages: null,
  displayZones: null,
};

/**
 * Parse a display layout's zones into an ordered list of zone ids
 * (position 0 = first zone, etc.). Returns [] if the layout has no config.
 */
const parseLayoutZoneIds = (
  layout: DisplayLayout | null | undefined,
): string[] => {
  if (!layout?.layoutConfig) return [];

  try {
    const config =
      typeof layout.layoutConfig === "string"
        ? JSON.parse(layout.layoutConfig)
        : layout.layoutConfig;

    return (config?.main?.zones || []).map((zone: { id: string }) => zone.id);
  } catch {
    return [];
  }
};

export const DisplayFormModal: React.FC<DisplayFormModalProps> = ({
  opened,
  onClose,
  display,
  venueId,
  onCreateDisplay,
  onUpdateDisplay,
  isCreating = false,
  isUpdating = false,
  lastError,
  onClearError,
}) => {
  const [formData, setFormData] = useState<DisplayFormData>(initialFormData);
  const [validationErrors, setValidationErrors] = useState<any>({});
  const [selectedLayout, setSelectedLayout] = useState<DisplayLayout | null>(
    null,
  );
  // Position-indexed stash of zone configs, preserved across layout changes so
  // switching layouts keeps each zone's configuration (by position) and does not
  // lose configs for zones that a smaller layout temporarily hides. Entries may
  // be `undefined` for positions that are unconfigured.
  const [zoneConfigStash, setZoneConfigStash] = useState<
    (DisplayZoneCardValue | undefined)[]
  >([]);

  const isEditMode = Boolean(display) && Boolean(display?.id);
  const isSubmitting = isCreating || isUpdating;

  // Convert DisplayRunningMessage to DisplayRunningMessageInput
  const convertDisplayRunningMessagesToInput = (
    displayRunningMessages?: DisplayRunningMessage[],
  ): DisplayRunningMessageInput[] => {
    if (!displayRunningMessages) return [];

    return displayRunningMessages.map((drm) => ({
      displayId: display?.id,
      runningMessageId: drm.runningMessageId,
      startDatetime: drm.startDatetime,
      endDatetime: drm.endDatetime,
      orderNo: drm.orderNo,
    }));
  };

  // Convert display zones to DisplayZoneCardValue format
  const convertDisplayZonesToCardValues = (
    displayZones?: any[],
  ): DisplayZoneCardValue[] => {
    if (!displayZones) return [];

    return displayZones.map((zone) => ({
      displayZoneId: zone.displayZoneId,
      displayZoneType: zone.displayZoneType,
      displayZoneTitle: zone.displayZoneTitle,
      playListId: zone.playListId,
      fallbackPlayListId: zone.fallbackPlayListId,
      scheduleType: zone.scheduleType,
      venueId: zone.venueId,
      roomId: zone.roomId,
      floor: zone.floor,
      roomTypeId: zone.roomTypeId,
      roomTag: zone.roomTag,
      eventTag: zone.eventTag,
      roomCategoryId: zone.roomCategoryId || undefined,
      eventCategoryId: zone.eventCategoryId || undefined,
      showPastHours: zone.showPastHours,
      showUpcomingHours: zone.showUpcomingHours,
    }));
  };

  // Reset form when modal opens/closes or display changes
  useEffect(() => {
    if (opened) {
      if (display) {
        // Edit mode - populate form with display data
        setFormData({
          venueId: display.venueId || venueId,
          layoutId: display.layoutId || null,
          themeId: display.themeId || null,
          name: display.name || "",
          description: display.description || "",
          emergencyContentBlockId: display.emergencyContentBlockId || null,
          blockTransitionAnimation: display.blockTransitionAnimation || null,
          status: display.status != null ? display.status : 100,
          displayRunningMessages: convertDisplayRunningMessagesToInput(
            display.displayRunningMessages,
          ),
          displayZones: convertDisplayZonesToCardValues(display.displayZones),
        });
      } else {
        // Create mode - reset to initial values with provided venueId
        const newFormData = { ...initialFormData };
        newFormData.venueId = venueId;
        setFormData(newFormData);
        setSelectedLayout(null);
      }
      // Start each editing session with a clean stash; it is (re)built from the
      // current zones the first time the layout actually changes.
      setZoneConfigStash([]);
      setValidationErrors({});
      onClearError?.();
    }
  }, [opened, display, onClearError, venueId]);

  const validateForm = (): boolean => {
    const newErrors: any = {};

    // Required fields
    if (!formData.name.trim()) {
      newErrors.name = "Display name is required";
    }

    if (!formData.layoutId) {
      newErrors.layoutId = "Display layout is required";
    }

    if (!formData.themeId) {
      newErrors.themeId = "Display theme is required";
    }

    // Validate display zones if layout is selected
    if (selectedLayout) {
      const zoneErrors: Record<string, any> = {};

      // Parse zones from layout
      try {
        const layoutConfig =
          typeof selectedLayout.layoutConfig === "string"
            ? JSON.parse(selectedLayout.layoutConfig)
            : selectedLayout.layoutConfig;

        const layoutZones = layoutConfig?.main?.zones || [];
        const configuredZones = formData.displayZones || [];

        // Check if all zones are configured
        layoutZones.forEach((layoutZone: any) => {
          const configuredZone = configuredZones.find(
            (z) => z.displayZoneId === layoutZone.id,
          );

          if (!configuredZone || !configuredZone.displayZoneType) {
            zoneErrors[layoutZone.id] = {
              displayZoneType: "Zone type is required",
            };
          } else {
            // Validate content zones
            if (
              configuredZone.displayZoneType === "content_blocks" &&
              !configuredZone.playListId
            ) {
              zoneErrors[layoutZone.id] = {
                playListId: "Playlist is required for content zone",
              };
            }

            // Validate schedule zones
            if (
              configuredZone.displayZoneType === "schedule" &&
              !configuredZone.scheduleType
            ) {
              zoneErrors[layoutZone.id] = {
                scheduleType: "Schedule type is required for schedule zone",
              };
            }
          }
        });

        if (Object.keys(zoneErrors).length > 0) {
          newErrors.displayZones = zoneErrors;
        }
      } catch (error) {
        console.error("Failed to validate zones:", error);
      }
    }

    setValidationErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (
    field: keyof DisplayFormData,
    value:
      | string
      | number
      | string[]
      | null
      | DisplayRunningMessageInput[]
      | DisplayZoneCardValue[],
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear validation error for this field when user starts typing
    if (validationErrors[field]) {
      setValidationErrors((prev: any) => ({
        ...prev,
        [field]: undefined,
      }));
    }
  };

  const handleLayoutChange = (
    layoutId: string | null,
    layout?: DisplayLayout,
  ) => {
    const previousLayout = selectedLayout;
    const previousLayoutId = formData.layoutId;

    setSelectedLayout(layout || null);

    // No real change (e.g. the selector resolving the initial value on load).
    // Just record the layout id and keep the existing zone configuration.
    if ((layoutId || "") === (previousLayoutId || "")) {
      handleInputChange("layoutId", layoutId || "");
      return;
    }

    // Layout actually changed: preserve zone configuration by POSITION.
    // - Same number of zones  -> every zone keeps its config (re-keyed to the
    //   new layout's zone ids).
    // - More zones            -> the first N zones keep their config, the rest
    //   start empty.
    // - Fewer zones           -> extra zones are hidden but stashed, so coming
    //   back to a larger layout restores them (until the user saves).
    const oldZoneIds = parseLayoutZoneIds(previousLayout);
    const newZoneIds = parseLayoutZoneIds(layout);
    const activeZones = formData.displayZones || [];

    // Refresh the stash for the positions the OLD layout showed — the active
    // zones are the source of truth for those (they include the user's edits).
    // Positions beyond the old layout (previously stashed overflow) are kept.
    const nextStash = [...zoneConfigStash];
    oldZoneIds.forEach((oldZoneId, index) => {
      nextStash[index] = activeZones.find((z) => z.displayZoneId === oldZoneId);
    });

    // Build the new active zones from the stash, re-keyed to the new zone ids.
    const nextActiveZones: DisplayZoneCardValue[] = [];
    newZoneIds.forEach((newZoneId, index) => {
      const stashed = nextStash[index];
      if (stashed) {
        nextActiveZones.push({ ...stashed, displayZoneId: newZoneId });
      }
    });

    setZoneConfigStash(nextStash);
    setFormData((prev) => ({
      ...prev,
      layoutId: layoutId || "",
      displayZones: nextActiveZones,
    }));
  };

  const handleThemeChange = (themeId: string | string[] | null) => {
    handleInputChange("themeId", themeId || "");
  };

  const handleEmergencyContentBlockChange = (
    blockId: string | null,
    _: ContentBlock | undefined,
  ) => {
    handleInputChange("emergencyContentBlockId", blockId || "");
  };

  const handleDisplayRunningMessagesChange = (
    assignments: DisplayRunningMessageInput[],
  ) => {
    handleInputChange("displayRunningMessages", assignments);
  };

  const handleDisplayZonesChange = (zones: DisplayZoneCardValue[]) => {
    handleInputChange("displayZones", zones);
  };

  // Get the error to display for a field
  const getDisplayError = (
    field: keyof DisplayFormData,
  ): string | number | undefined => {
    return validationErrors[field];
  };

  const handleSubmit = async () => {
    // Clear errors on new submission
    onClearError?.();

    if (!validateForm()) {
      return;
    }

    try {
      // Convert DisplayZoneCardValue to DisplayZoneInput
      const displayZonesInput: DisplayZoneInput[] | undefined =
        formData.displayZones && formData.displayZones.length > 0
          ? formData.displayZones.map((zone) => ({
              displayId: display?.id,
              displayZoneId: zone.displayZoneId,
              displayZoneType: zone.displayZoneType || undefined,
              displayZoneTitle: zone.displayZoneTitle || undefined,
              playListId: zone.playListId || undefined,
              fallbackPlayListId: zone.fallbackPlayListId || undefined,
              scheduleType: zone.scheduleType || undefined,
              venueId: zone.venueId || undefined,
              roomId: zone.roomId || undefined,
              floor: zone.floor || undefined,
              roomTypeId: zone.roomTypeId || undefined,
              roomTag: zone.roomTag || undefined,
              eventTag: zone.eventTag || undefined,
              roomCategoryId: zone.roomCategoryId || undefined,
              eventCategoryId: zone.eventCategoryId || undefined,
              showPastHours: zone.showPastHours || undefined,
              showUpcomingHours: zone.showUpcomingHours || undefined,
            }))
          : undefined;

      // Prepare display data
      const displayData: DisplayInput = {
        venueId: formData.venueId,
        layoutId: formData.layoutId || null,
        themeId: formData.themeId || null,
        name: formData.name,
        description: formData.description || null,
        emergencyContentBlockId: formData.emergencyContentBlockId || null,
        blockTransitionAnimation: formData.blockTransitionAnimation || null,
        status: formData.status,
        displayRunningMessages:
          formData.displayRunningMessages &&
          formData.displayRunningMessages.length > 0
            ? formData.displayRunningMessages
            : null,
        displayZones: displayZonesInput,
      };

      let result: Display | null = null;

      if (isEditMode && display && onUpdateDisplay) {
        // Update existing display
        result = await onUpdateDisplay(display.id, displayData);
      } else if (!isEditMode && onCreateDisplay) {
        // Create new display
        result = await onCreateDisplay(displayData);
      }

      // If successful, close the modal
      if (result) {
        onClose();
      }
    } catch (error) {
      // Error handling is managed by the hook/parent component
      console.error("Form submission error:", error);
    }
  };

  const handleCancel = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  // Render generic errors
  const renderGenericErrors = () => {
    if (!lastError) {
      return null;
    }

    return (
      <Alert
        color="red"
        icon={<AppIcon iconName="AlertCircle" size={16} />}
        title="Error"
      >
        <Text size="sm">{lastError}</Text>
      </Alert>
    );
  };

  return (
    <AppModal
      opened={opened}
      onClose={handleCancel}
      title={isEditMode ? "Edit Display" : "Add New Display"}
      size="lg"
      loading={isSubmitting}
      actions={[
        {
          label: "Cancel",
          variant: "secondary",
          onClick: handleCancel,
          disabled: isSubmitting,
        },
        {
          label: "Confirm",
          variant: "primary",
          iconName: "Check",
          onClick: handleSubmit,
          inProgress: isSubmitting,
        },
      ]}
    >
      <Stack gap="md">
        {/* Basic Information */}
        <Grid>
          <Grid.Col span={12}>
            <TextInput
              label="Display Name"
              placeholder="Enter display name"
              value={formData.name}
              onChange={(e) => handleInputChange("name", e.target.value)}
              error={getDisplayError("name")}
              required
              withAsterisk
              size="md"
            />
          </Grid.Col>

          <Grid.Col span={12}>
            <Textarea
              label="Description"
              placeholder="Enter display description"
              value={formData.description}
              onChange={(e) => handleInputChange("description", e.target.value)}
              error={getDisplayError("description")}
              rows={2}
              size="md"
            />
          </Grid.Col>
        </Grid>

        {/* Display Configuration */}
        <Grid>
          <Grid.Col span={12}>
            <Title order={5}>Display Configuration</Title>
          </Grid.Col>

          <Grid.Col span={{ base: 12, sm: 6 }}>
            <DisplayLayoutSelector
              label="Display Layout"
              value={formData.layoutId || null}
              onChange={handleLayoutChange}
              placeholder="Select a display layout"
              error={getDisplayError("layoutId")}
              required
              withAsterisk
              clearable={false}
            />
          </Grid.Col>

          <Grid.Col span={{ base: 12, sm: 6 }}>
            <ThemeSelector
              label="Display Theme"
              value={formData.themeId || null}
              onChange={handleThemeChange}
              placeholder="Select a display theme"
              error={getDisplayError("themeId")}
              required
              withAsterisk
              clearable={false}
            />
          </Grid.Col>
        </Grid>

        {/* Display Zones Configuration */}
        {formData.layoutId && (
          <Grid>
            <Grid.Col span={12}>
              <Title order={5}>Zone Configuration</Title>
              <DisplayZoneAssignments
                layout={selectedLayout}
                value={formData.displayZones}
                onChange={handleDisplayZonesChange}
                venueId={formData.venueId}
                errors={validationErrors.displayZones}
                disabled={isSubmitting}
              />
            </Grid.Col>
          </Grid>
        )}

        {/* Emergency Content Block */}
        <Grid>
          <Grid.Col span={12}>
            <Title order={5}>Emergency Content</Title>
          </Grid.Col>

          <Grid.Col span={12}>
            <ContentBlockSelector
              label="Emergency Content Block"
              value={formData.emergencyContentBlockId}
              onChange={handleEmergencyContentBlockChange}
              venueId={formData.venueId}
              placeholder="Select emergency content block (optional)"
              error={getDisplayError("emergencyContentBlockId")}
            />
            <Text size="xs" c="dimmed" mt="xs">
              This content will be displayed overriding all other content
            </Text>
          </Grid.Col>
        </Grid>

        {/* Running Message Assignments */}
        <Grid>
          <Grid.Col span={12}>
            <Title order={5}>Running Message Assignments</Title>
          </Grid.Col>
          <Grid.Col span={12}>
            <DisplayRunningMessageSelector
              displayId={isEditMode ? display?.id : null}
              value={formData.displayRunningMessages}
              onChange={handleDisplayRunningMessagesChange}
              placeholder="Manage running message assignments with scheduling"
              error={getDisplayError("displayRunningMessages")}
            />
          </Grid.Col>
        </Grid>

        {/* Block Transition Animation */}
        <Grid>
          <Grid.Col span={12}>
            <Stack gap={0}>
              <InputLabel required>Block Transition Animation</InputLabel>
              <BlockTransitionAnimationSelector
                value={formData.blockTransitionAnimation}
                onChange={(value) =>
                  handleInputChange("blockTransitionAnimation", value)
                }
                error={getDisplayError("blockTransitionAnimation")}
                disabled={isSubmitting}
              />
            </Stack>
          </Grid.Col>
        </Grid>

        <Grid>
          <Grid.Col span={12}>
            <Stack gap={0}>
              <InputLabel required>Status</InputLabel>
              <StatusSelector
                value={formData.status}
                onChange={(value) =>
                  handleInputChange("status", Number(value) || 100)
                }
                error={getDisplayError("status")}
                required
              />
            </Stack>
          </Grid.Col>
        </Grid>

        {/* Generic errors */}
        {renderGenericErrors()}
      </Stack>
    </AppModal>
  );
};
